/**
 * Who gets Settings → App settings (CONFIG CF-10): the server's rule for reading a settings group
 * (services/settings/settings-access.ts), mirrored so the card and the route hide what the API would
 * refuse. A Super Admin or AIG staff (useSkipsPermissionChecks) always; anyone else, Admin included,
 * holding a group's edit key or one of its read keys through a role. Exact permissions only, no tier
 * fallback: a key no role holds yet admits only those who skip checks, as on the server.
 * An `aigOnly` group (every ai.*: Noah 2026-10-16) counts only for an AIG account (useIsSuperAdmin),
 * and the page drops it for anyone else (isAppSettingsGroupVisible). A group edited on its own page
 * (`editedOn`, e.g. dashboards.layouts on the Roles tab) is not on App settings, so its key does not count.
 * The API decides every call itself; this is UX, not security.
 */
import { SETTINGS_REGISTRY } from '@aiga/app-config/settings';
import { useAuth } from '@/contexts/AuthContext';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import { useSkipsPermissionChecks } from '@/hooks/useSkipsPermissionChecks';

const readKeys = (aig: boolean): ReadonlySet<string> =>
  new Set(SETTINGS_REGISTRY.groups.filter((g) => (aig || !g.aigOnly) && !g.editedOn).flatMap((g) => [g.editPermission, ...g.readPermission]));
const READ_KEYS = readKeys(false);
const AIG_READ_KEYS = readKeys(true);

export function useCanSeeAppSettings(): boolean {
  const { permissions } = useAuth();
  const skips = useSkipsPermissionChecks();
  const keys = useIsSuperAdmin() ? AIG_READ_KEYS : READ_KEYS;
  return skips || (permissions ?? []).some((p) => keys.has(p.name));
}

/**
 * A group the server sent stays off the page when the registry marks it AIG-only and the user is not AIG,
 * or when another page edits it (`editedOn`).
 */
export function isAppSettingsGroupVisible(groupId: string, isAig: boolean): boolean {
  const group = SETTINGS_REGISTRY.group(groupId);
  return (isAig || !group?.aigOnly) && !group?.editedOn;
}
